// BHP-1046: HTTP client for Backend2.0 (API Platform).
// - base `/api` (no `/api/v1`), from env
// - Bearer token from the Authentik session (cookie set by AuthCheckProvider)
// - `x-active-customer` header from the active-customer store (BHP-1081), so the
//   backend narrows account-wide collections to the active customer
// - `application/problem+json` (RFC 7807) mapped to ApiError
import env from '../../config/env';
import { useActiveCustomerStore } from '../../features/customer/application/activeCustomerStore';
import TokenService from '../../Services/TokenService';
import { ApiError, type FieldViolation } from './ApiError';

type TokenProvider = () => string | null;
type ActiveCustomerProvider = () => string | null;

/** BHP-1081: header carrying the active customer, aligned with the legacy stack. */
const ACTIVE_CUSTOMER_HEADER = 'x-active-customer';

/**
 * BHP-1144: a per-request reference id, generated here and attached to every
 * request. The backend tags its logs and Sentry events with it, and on a 5xx
 * the UI shows this same id — so a customer who quotes it lets support find
 * the real failure (a Hetzner 403, an Exact refusal) without ever seeing a
 * provider name. It lands on the ApiError so the message layer can render it.
 */
const CLIENT_REQUEST_ID_HEADER = 'X-Client-Request-Id';

function newClientRequestId(): string {
  // crypto.randomUUID is available in every browser this app supports; the
  // fallback keeps tests and any exotic runtime from throwing.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface HttpClientOptions {
  /** Full API base including the `/api` prefix. Defaults to `${VITE_API_BASE_URL}/api`. */
  baseUrl?: string;
  getToken?: TokenProvider;
  /** Active-customer UUID to send as `x-active-customer`; null omits the header. */
  getActiveCustomer?: ActiveCustomerProvider;
  /** Injected for tests; defaults to the global fetch. */
  fetchFn?: typeof fetch;
}

/** BHP-1160: a downloaded document plus the name the backend gave it. */
export interface ApiFile {
  blob: Blob;
  /** Null when the server sent no usable Content-Disposition. */
  fileName: string | null;
}

export interface RequestOptions {
  params?: Record<string, string | number | undefined>;
  /** Override the default `application/ld+json` Accept (e.g. plain JSON endpoints). */
  accept?: string;
  /** Override the request body Content-Type (e.g. merge-patch for PATCH). */
  contentType?: string;
}

const JSON_LD = 'application/ld+json';
/** API Platform PATCH bodies are partial documents (RFC 7386). */
const MERGE_PATCH = 'application/merge-patch+json';

export class HttpClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly getActiveCustomer: ActiveCustomerProvider;
  private readonly fetchFn: typeof fetch;

  constructor(options: HttpClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? `${env.apiBaseUrl}/api`;
    this.getToken =
      options.getToken ?? (() => TokenService.getAccessTokenCookie());
    this.getActiveCustomer =
      options.getActiveCustomer ??
      (() => useActiveCustomerStore.getState().activeCustomerUuid);
    this.fetchFn = options.fetchFn ?? ((...args) => fetch(...args));
  }

  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return await this.request<T>('GET', path, undefined, options);
  }

  async post<T>(
    path: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>('POST', path, body, options);
  }

  async put<T>(
    path: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>('PUT', path, body, options);
  }

  /** Partial update as `application/merge-patch+json` (API Platform PATCH). */
  async patch<T>(
    path: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>('PATCH', path, body, {
      ...options,
      contentType: options.contentType ?? MERGE_PATCH
    });
  }

  async delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return await this.request<T>('DELETE', path, undefined, options);
  }

  /**
   * BHP-1160: a binary response (a PDF) instead of JSON, with the filename the
   * backend chose for it.
   *
   * This exists because `request()` always parses the body as JSON, and because
   * an `<iframe src="/api/...">` cannot carry the Bearer token — the browser
   * sends no Authorization header on a subresource load, so the only way to
   * render a protected document is to fetch it here and hand the viewer an
   * object URL. Errors still come back as ApiError: an authorization failure on
   * a document has to read the same as one anywhere else.
   *
   * The name travels in Content-Disposition rather than being rebuilt here.
   * The backend already decides it in one place (InvoiceAttachmentNaming), and
   * the same document goes out as a mail attachment under that name — a second
   * spelling in the browser is how a customer ends up with two differently
   * named copies of one invoice.
   */
  async getFile(path: string, options: RequestOptions = {}): Promise<ApiFile> {
    const requestId = newClientRequestId();
    const headers: Record<string, string> = {
      Accept: options.accept ?? 'application/pdf',
      [CLIENT_REQUEST_ID_HEADER]: requestId
    };

    const token = this.getToken();
    if (token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }
    const activeCustomer = this.getActiveCustomer();
    if (activeCustomer !== null) {
      headers[ACTIVE_CUSTOMER_HEADER] = activeCustomer;
    }

    const response = await this.fetchOrThrow(
      this.buildUrl(path, options.params),
      { method: 'GET', mode: 'cors', headers },
      requestId
    );

    if (!response.ok) {
      throw await toApiError(response, requestId);
    }

    const blob = await response.blob();
    const expected = options.accept ?? 'application/pdf';

    // The caller hands this blob to an <iframe> as an object URL, and a blob:
    // URL inherits the app's origin. A 200 whose body is not what we asked for
    // — an interstitial from a proxy or WAF, an HTML error page from something
    // in between — would then render as same-origin markup inside the portal.
    // `response.ok` does not rule that out; this does.
    if (blob.type !== '' && !blob.type.startsWith(expected)) {
      // BHP-1144: its own kind, so the message layer shows the caller's
      // fallback (never this technical title) without a reference — the
      // request did not reach our backend — and the document query does not
      // retry a page that would come back the same.
      throw new ApiError({
        title: 'Unexpected document',
        detail: '',
        status: response.status,
        kind: 'invalid-body',
        requestId
      });
    }

    return {
      blob,
      fileName: fileNameFrom(response.headers.get('content-disposition'))
    };
  }

  /**
   * BHP-1144: a `fetch` that rejects means the request never reached the
   * backend — the server is down, DNS failed, the connection dropped. That is
   * a transport failure, not an application error, and it escapes as a raw
   * TypeError the message layer cannot read. Turn it into an ApiError with
   * status 0 so one branch covers "unreachable" and "5xx" alike, carrying the
   * same reference id (shown to nobody: the backend never saw it). A non-Error
   * rejection (should not happen) is re-thrown.
   */
  private async fetchOrThrow(
    url: string,
    init: RequestInit,
    requestId: string
  ): Promise<Response> {
    try {
      return await this.fetchFn(url, init);
    } catch (cause) {
      // fetch rejects only on a transport failure (TypeError/DOMException),
      // never with an ApiError — toApiError runs after this returns. A
      // non-Error rejection (should not happen) is passed through untouched.
      if (cause instanceof Error) {
        throw new ApiError({
          title: 'Could not reach the server',
          detail: '',
          status: 0,
          kind: 'transport',
          requestId,
          // Kept for debugging: CORS, DNS and a reset all look alike here.
          cause
        });
      }
      throw cause;
    }
  }

  private buildUrl(path: string, params?: RequestOptions['params']): string {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${this.baseUrl}${normalized}`);
    if (params !== undefined) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      }
    }
    return url.toString();
  }

  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    options: RequestOptions
  ): Promise<T> {
    const requestId = newClientRequestId();
    const headers: Record<string, string> = {
      Accept: options.accept ?? JSON_LD,
      [CLIENT_REQUEST_ID_HEADER]: requestId
    };

    const token = this.getToken();
    if (token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }
    const activeCustomer = this.getActiveCustomer();
    if (activeCustomer !== null) {
      headers[ACTIVE_CUSTOMER_HEADER] = activeCustomer;
    }
    if (body !== undefined) {
      headers['Content-Type'] = options.contentType ?? JSON_LD;
    }

    const response = await this.fetchOrThrow(
      this.buildUrl(path, options.params),
      {
        method,
        mode: 'cors',
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined
      },
      requestId
    );

    if (!response.ok) {
      throw await toApiError(response, requestId);
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }
}

async function toApiError(
  response: Response,
  requestId: string
): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/problem+json')) {
    const problem = await response.json().catch(() => null);
    if (problem !== null && typeof problem === 'object') {
      return new ApiError({
        title:
          typeof problem.title === 'string' ? problem.title : 'Request failed',
        detail: typeof problem.detail === 'string' ? problem.detail : '',
        status:
          typeof problem.status === 'number' ? problem.status : response.status,
        violations: parseViolations(problem.violations),
        requestId
      });
    }
  }
  // Network/5xx without problem+json: generic error, never crash on missing
  // fields. Not problem+json means something in front of Backend2.0 answered
  // (an ingress or proxy error page), so the backend never logged requestId.
  return new ApiError({
    title: 'Request failed',
    detail: '',
    status: response.status,
    requestId,
    fromBackend: false
  });
}

/** Map the API Platform `violations` array to field-level errors, ignoring malformed entries. */
function parseViolations(raw: unknown): FieldViolation[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const violations: FieldViolation[] = [];
  for (const entry of raw) {
    if (
      entry !== null &&
      typeof entry === 'object' &&
      typeof entry.propertyPath === 'string' &&
      typeof entry.message === 'string'
    ) {
      violations.push({
        propertyPath: entry.propertyPath,
        message: entry.message
      });
    }
  }
  return violations;
}

/** Shared default client (Bearer from the session cookie). */
export const httpClient = new HttpClient();

/**
 * BHP-1160: pull the filename out of a Content-Disposition header.
 *
 * `filename*` (RFC 5987) wins when present — it is the one that survives
 * non-ASCII, and a company name is exactly where that shows up. Anything
 * unparseable yields null so the caller falls back rather than offering a file
 * called "undefined".
 */
export function fileNameFrom(disposition: string | null): string | null {
  if (disposition === null) {
    return null;
  }

  const extended = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (extended !== null) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // A malformed percent-escape is not worth throwing over.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);

  return plain === null ? null : plain[1];
}
